/* Starter Pack add to cart — shared module (window.StarterPackCart)
 * Same cart lines as the Starter Pack PDP (assets/starter-pack-pdp.js addToCart / postCart):
 * - Subscribe: each coffee is its own line on its own selling plan, linked to the Recharge
 *   dynamic bundle with recharge.bundle.getDynamicBundleItems (_rc_bundle properties), plus the
 *   free Flask as a _starter_gift line in the same _starter_gift_group.
 * - One-off: the two coffees at full price, no Recharge bundle, no Flask.
 * Copied from the PDP rather than shared with it for now: the PDP keeps its own copy until the
 * Starter Pack templates are cleaned up, then it should move onto this module too.
 * Adds `source` to the add_to_cart_starter_pack dataLayer event and a hidden `_source` line
 * property so adds can be reported by where they came from.
 */
(() => {
  if (window.StarterPackCart) return;

  const SDK_URL = 'https://static.rechargecdn.com/assets/storefront/recharge-client-1.43.0.min.js';
  let sdkPromise;
  const bundleCache = new Map();

  const loadSdk = (storeIdentifier) => {
    if (!sdkPromise) {
      sdkPromise = new Promise((resolve, reject) => {
        const ready = () => {
          try {
            window.recharge.init({ storeIdentifier, appName: 'truestart-starter-pack-home', appVersion: '1.0.0' });
          } catch (e) { /* already initialised by another app */ }
          resolve(window.recharge);
        };
        if (window.recharge && window.recharge.bundle) return ready();
        const s = document.createElement('script');
        s.src = SDK_URL;
        s.async = true;
        s.onload = ready;
        s.onerror = () => { sdkPromise = null; reject(new Error('Recharge SDK failed to load')); };
        document.head.appendChild(s);
      });
    }
    return sdkPromise;
  };

  const track = (event, data = {}) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event, ...data });
  };

  // Bundle collections from the Recharge CDN, only needed for the subscription add (cached per bundle).
  const fetchBundle = async ({ storeIdentifier, bundle }) => {
    const key = `${bundle.productId}:${bundle.variantId}`;
    if (bundleCache.has(key)) return bundleCache.get(key);
    let data;
    try {
      const res = await fetch(`https://static.rechargecdn.com/store/${storeIdentifier}/product/2020-12/${bundle.productId}.json`);
      if (!res.ok) throw new Error(res.status);
      const json = await res.json();
      const bp = json.product && json.product.bundle_product;
      if (!bp) throw new Error('No bundle_product');
      const variant = (bp.variants || []).find((v) => String(v.external_variant_id) === String(bundle.variantId)) || bp.variants[0];
      data = { collections: (variant.option_sources || []).map((o) => String(o.collection_id)) };
    } catch (e) {
      console.warn('[Starter Pack] Recharge bundle data unavailable, using fallback collections', e);
      data = { collections: (bundle.fallbackCollections || []).map((c) => String(c).trim()).filter(Boolean) };
    }
    bundleCache.set(key, data);
    return data;
  };

  // Some coffees carry two plan groups per cadence (discounted Recharge plan + a full-price one): use the cheapest.
  const planFor = (coffee, weeks) => {
    const matches = ((coffee && coffee.plans) || []).filter((p) => Number(p.weeks) === Number(weeks));
    return matches.sort((x, y) => Number(x.price) - Number(y.price))[0] || null;
  };

  const collectionFor = (collections, coffee, slot, used) => {
    const mine = (coffee.collections || []).map(String);
    // prefer the collection matching the slot position, then any bundle collection the product is in
    const preferred = collections[slot - 1];
    if (preferred && mine.includes(preferred) && !used.includes(preferred + ':' + coffee.variantId)) return preferred;
    return collections.find((c) => mine.includes(c)) || preferred || collections[0];
  };

  const postCart = async (items) => {
    // Prefer the theme's cart API: it adds, re-renders the side cart and opens it in one flow.
    const themeCart = window.cart;
    if (themeCart && typeof themeCart.add === 'function') {
      await themeCart.add({ items }, { requestName: 'starter-pack-add' });
      return;
    }
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const res = await fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.description || err.message || `Cart error ${res.status}`);
    }
    window.location.href = `${root}cart`;
  };

  /**
   * @param {object} opts
   * @param {object} opts.cfg     { storeIdentifier, bundle: { productId, variantId, handle, fallbackCollections }, mug, text: { cartLabel, partOf, onceLabel } }
   * @param {object[]} opts.coffees  the two picked coffees: { name, productId, variantId, plans: [{ id, weeks, price }], collections }
   * @param {number|null} opts.weeks  cadence for a subscription; null for a one-off
   * @param {string} opts.source  where the add came from, e.g. 'homepage_widget'
   */
  const add = async ({ cfg, coffees, weeks, source }) => {
    const [a, b] = coffees;
    const text = cfg.text || {};
    const partOf = text.partOf || 'Part of';
    const sourceProps = source ? { _source: source } : {};
    const names = `${a.name} + ${b.name}`;

    if (!weeks) {
      // One-off purchase: just the two coffees at full price. No subscription, no Recharge bundle, no Flask.
      const onceItems = [];
      [a, b].forEach((coffee) => {
        const existing = onceItems.find((it) => String(it.id) === String(coffee.variantId));
        if (existing) existing.quantity += 1;
        else onceItems.push({ id: Number(coffee.variantId), quantity: 1, properties: { [partOf]: text.onceLabel || 'Starter Pack (one-off)', ...sourceProps } });
      });
      await postCart(onceItems.reverse());
      track('add_to_cart_starter_pack', { mode: 'one-off', weeks: null, coffees: names, source });
      return;
    }

    const [bundleData, rc] = await Promise.all([fetchBundle(cfg), loadSdk(cfg.storeIdentifier)]);
    const bundleCfg = cfg.bundle;
    // The same coffee picked twice becomes one selection with quantity 2
    const picks = [];
    [a, b].forEach((coffee, i) => {
      const existing = picks.find((p) => String(p.coffee.variantId) === String(coffee.variantId));
      if (existing) existing.quantity += 1;
      else picks.push({ coffee, slot: i + 1, quantity: 1 });
    });
    const used = [];
    const selections = picks.map(({ coffee, slot, quantity }) => {
      const plan = planFor(coffee, weeks);
      if (!plan) throw new Error(`No ${weeks}-week plan for ${coffee.name}`);
      const collectionId = collectionFor(bundleData.collections, coffee, slot, used);
      used.push(collectionId + ':' + coffee.variantId);
      return {
        collectionId: String(collectionId),
        externalProductId: String(coffee.productId),
        externalVariantId: String(coffee.variantId),
        quantity,
        sellingPlan: Number(plan.id),
      };
    });
    const bundle = {
      externalProductId: String(bundleCfg.productId),
      externalVariantId: String(bundleCfg.variantId),
      selections,
    };
    if (rc.bundle && typeof rc.bundle.validateDynamicBundle === 'function') {
      const valid = rc.bundle.validateDynamicBundle(bundle);
      if (valid !== true) throw new Error(typeof valid === 'string' ? valid : 'Invalid Starter Pack selection');
    }
    let lines;
    if (rc.bundle && typeof rc.bundle.getDynamicBundleItems === 'function') {
      lines = await rc.bundle.getDynamicBundleItems(bundle, bundleCfg.handle);
    } else {
      // Fallback: same line properties the Recharge SDK generates for dynamic bundles
      const rcId = `${Math.random().toString(36).slice(2, 12)}:${bundleCfg.productId}`;
      lines = selections.map((sel) => ({
        id: Number(sel.externalVariantId),
        quantity: sel.quantity,
        selling_plan: sel.sellingPlan,
        properties: {
          _rc_bundle: rcId,
          _rc_bundle_variant: String(bundleCfg.variantId),
          _rc_bundle_parent: bundleCfg.handle,
          _rc_bundle_collection_id: sel.collectionId,
        },
      }));
    }
    const group = (lines[0] && lines[0].properties && lines[0].properties._rc_bundle) || `spp-${Date.now()}`;
    const label = text.cartLabel || 'Starter Pack';
    // "Part of: Starter Pack" is visible in the cart, checkout and order confirmation.
    lines = lines.map((line) => ({
      ...line,
      properties: { ...(line.properties || {}), [partOf]: label, _starter_pack: group, _starter_gift_group: group, _starter_pack_size: '2', ...sourceProps },
    }));
    // Shopify lists lines added in one request newest-first, so the gift goes in first
    // to make the cart read: coffee, coffee -> free Flask.
    const items = [];
    if (cfg.mug && cfg.mug.available) {
      items.push({
        id: cfg.mug.variantId,
        quantity: 1,
        properties: { _starter_gift: 'true', _starter_gift_group: group, _starter_pack: group, _gift_note: 'Free with your Starter Pack', [partOf]: label, ...sourceProps },
      });
    }
    items.push(...lines);
    await postCart(items);
    track('add_to_cart_starter_pack', { mode: 'subscription', weeks, coffees: names, source });
  };

  window.StarterPackCart = { add, planFor, track, loadSdk };
})();
